import React, { useState, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'semantic-ui-react/dist/commonjs/modules/Modal';
import MediaMinWidth from '../common/media_min_width';
import UserContext from '../../contexts/user';
import ShownPopupsContext from '../../contexts/popups_context';
import FlexWrapper from '../common/flex';
import UIMediaAndContentCard from '../ui/media_and_content_card';
import UIMinimalTextButton from '../ui/minimal_button';
import SubscribeBox from '../subscribe_box/subscribe_box';
import ContextAwarePopup from './detail/context_aware_popup';
import { breakpoints, mediaSizePolicy, sourceMediaSizeSpec } from './detail/content_card_params';
import {
  POPUP_MINIMUM_DELAY_BEFORE_SHOW_MS, POPUP_MINIMUM_SCROLL_BEFORE_SHOW_PX, POPUP_BACKOFF_SCHEDULE
} from './detail/general_params';
import { useTranslation } from '../common/localized';
import { getPopupMedia } from './detail/get_popup_media';

export const POPUP_TAG = 'subscribe-to-newsletter';

const SubscribePopup = ({ webPopup, timeToAppear }) => {
  const [showing, setShowing] = useState(false);
  const onClose = useCallback(() => setShowing(false), []);
  const [subscribed, setSubscribed] = useState(false);
  const { user } = useContext(UserContext);
  const shownPopupsContainer = useContext(ShownPopupsContext);
  const { t } = useTranslation();

  useEffect(() => {
    const lastDateShown = new Date(shownPopupsContainer.lastPopupShowTime(POPUP_TAG));
    const currentDate = new Date();
    // convert python date to js date
    const date = timeToAppear.split('T');
    const timeDelay = new Date(date[0] + ' ' + date[1].slice(0, -6));
    if (currentDate >= Date.parse(timeDelay) && lastDateShown <= Date.parse(timeDelay)) setShowing(true);
  }, [shownPopupsContainer, timeToAppear]);

  // If the user subscribes, then no action is needed in the future.
  const onSubscribe = useCallback(() => {
    shownPopupsContainer.markAsNoFurtherActionNeeded(POPUP_TAG);
    setSubscribed(true);
  }, [shownPopupsContainer]);

  return (
    <MediaMinWidth breakpoints={breakpoints}>
      {(...breakpointFlags) =>
        <ContextAwarePopup showing={showing} setShowing={setShowing} tag={POPUP_TAG}
          shownPopupsContainer={shownPopupsContainer} backoffPeriodSchedule={POPUP_BACKOFF_SCHEDULE}
          minimumDelayToShow={POPUP_MINIMUM_DELAY_BEFORE_SHOW_MS} shouldShowAutomatically={!user}
          minimumScrollToShow={POPUP_MINIMUM_SCROLL_BEFORE_SHOW_PX} timeToAppear={timeToAppear}>
          <Modal open={showing} onClose={onClose} closeOnDocumentClick={false} closeOnDimmerClick={false} basic>
            <FlexWrapper heightFluid>
              <Wrapper>
                <UIMediaAndContentCard media={getPopupMedia(webPopup, breakpoints, breakpointFlags)}
                  breakpoints={breakpoints}
                  breakpointFlags={breakpointFlags}
                  mediaSizePolicy={mediaSizePolicy}
                  sourceMediaSizeSpec={sourceMediaSizeSpec}
                  fullHeight noPadding>
                  <FlexWrapper vertical style={{ margin: '16px 0 16px 0' }}>
                    <Title>{t(webPopup.title)}</Title>
                    <Subtitle>{t(webPopup.subtitle)}</Subtitle>
                    <Text>{t(webPopup.text)}</Text>
                    <SubscribeBox noName forPopup content={webPopup.button_text} removeTitle
                      onSubscribe={onSubscribe} />
                    {!subscribed && <CancelPopup onClick={onClose}>{t('no thanks')}</CancelPopup>}
                    {subscribed && <UIMinimalTextButton content={t('Close')} onClick={onClose} />}
                  </FlexWrapper>
                </UIMediaAndContentCard>
              </Wrapper>
            </FlexWrapper>
          </Modal>
        </ContextAwarePopup>}
    </MediaMinWidth>
  );
};

const Wrapper = styled.div`
  position: relative;
  color: black;
`;

const Title = styled.p`
  font-size: 38px;
  font-weight: bold;
  line-height: 24px;
  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.p`
  font-size: 32px;
  font-style: italic;
  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`;

const Text = styled.p`
  margin: 24px 0 40px 0;
  text-align: center;
  font-weight: 600;
  @media screen and (max-width: 768px) {
    margin: 8px 0 14px 0;
    font-size: 12px;
    width: 80%;
  }
`;

const CancelPopup = styled.div`
  cursor: pointer;
  color: gray;
  font-size: 18px;
  margin-top: 6px;
  border-bottom: 2px solid gray;
  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

export default SubscribePopup;
